/**
 * TransferDocumentsDialog — modal das DUAS ações que precisam escolher um
 * destino: "Copiar para outra base" e "Mover para outra base".
 *
 * "Duplicar aqui" NÃO passa por aqui: é a mesma rota de copiar com destino
 * igual à base atual, disparada direto pela tela (spec §7).
 *
 * Anatomia copiada de `ExportPdfDialog`/`DeleteDocumentDialog` deste mesmo
 * módulo (portal para o body, z-[60], tema claro FIXO em hex, NÃO fecha ao
 * clicar fora — DS §Overlays dismissal) mais o bloco `LIGHT_THEME_OVERRIDE` do
 * `TransferTicketDialog`, que é o que faz o `Select` compartilhado ficar claro
 * dentro do modal (CLAUDE.md NUNCA #10: modal SEMPRE tema claro).
 *
 * Campos um abaixo do outro, nunca lado a lado (padrão de formulário da casa).
 * `Select` compartilhado com a lista pela prop `options` — NUNCA `<option>`
 * como filho (a interface tipa `children` como `never`).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import type {
  DatabaseDocumentTransferItemResult,
  DatabaseDocumentTransferRejectionCode,
  DatabaseDocumentTransferResult,
  DatabaseType,
} from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { Select, type SelectOption } from '@/shared/components/ui';
import { extractApiFieldErrors } from '@/shared/utils/apiFieldErrors';
import { useDatabasesDropdown } from '../hooks/useDatabasesDropdown';
import { useCopyDatabaseDocuments, useMoveDatabaseDocuments } from '../hooks/useDatabaseDocuments';
import { buildDatabaseTargetLabel } from './databaseTypeMeta';

export type TransferMode = 'copy' | 'move';

export interface TransferDocumentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** `copy` = copiar para outra base; `move` = mover para outra base. */
  mode: TransferMode;
  /** Base aberta na tela — sai da lista de destinos e serve para invalidar o cache. */
  sourceDatabaseId: string;
  /** Tipo da base aberta — o destino tem que ser do mesmo tipo (D5). */
  databaseType: DatabaseType;
  /** Alvos: 1 id (menu •••) ou N ids (barra de seleção). */
  documentIds: string[];
  /**
   * id → título, montado pela tela com a página JÁ carregada. É o que deixa o
   * resultado dizer "Casa da Praia veio do ERP" sem nenhuma busca extra.
   */
  titleById: Record<string, string>;
  /** Chamado quando o lote teve pelo menos 1 sucesso (a tela limpa a seleção). */
  onTransferred?: () => void;
}

/** Tema claro fixo do modal — o `Select` compartilhado lê estas variáveis. */
const LIGHT_THEME_OVERRIDE = {
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-error-color': '#ef4444',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-primary': '#8b5cf6',
  '--theme-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-component-background': '#ffffff',
} as React.CSSProperties;

/**
 * Motivo em português por código do backend. A API devolve ENUM, nunca frase —
 * é o que deixa a tela escrever o motivo sem interpretar texto do servidor.
 */
const REJECTION_LABELS: Record<DatabaseDocumentTransferRejectionCode, string> = {
  not_found: 'não foi encontrado (pode ter sido excluído)',
  wrong_type: 'é de outro tipo de base',
  inactive_target: 'a base de destino está inativa ou arquivada',
  integration_target: 'a base de destino é de integração',
  integration_linked: 'veio do ERP e não pode ser movido',
  same_database: 'já está nessa base',
  write_failed: 'falhou ao gravar',
};

interface ModeText {
  title: (count: number) => string;
  subtitle: string;
  icon: IconName;
  confirmLabel: string;
  pendingLabel: string;
  doneOne: string;
  doneMany: string;
  failOne: string;
  failMany: string;
}

const MODE_TEXT: Record<TransferMode, ModeText> = {
  copy: {
    title: (count) =>
      count === 1 ? 'Copiar registro para outra base' : `Copiar ${count} registros para outra base`,
    subtitle: 'Nascem registros novos na base de destino. O original continua onde está.',
    icon: 'copy',
    confirmLabel: 'Copiar',
    pendingLabel: 'Copiando…',
    doneOne: 'registro copiado',
    doneMany: 'registros copiados',
    failOne: 'não foi copiado',
    failMany: 'não foram copiados',
  },
  move: {
    title: (count) =>
      count === 1 ? 'Mover registro para outra base' : `Mover ${count} registros para outra base`,
    subtitle: 'O mesmo registro troca de base — nada muda dentro do dado.',
    icon: 'corner-up-right',
    confirmLabel: 'Mover',
    pendingLabel: 'Movendo…',
    doneOne: 'registro movido',
    doneMany: 'registros movidos',
    failOne: 'não foi movido',
    failMany: 'não foram movidos',
  },
};

/** Rótulo em português do campo de identidade removido na cópia. */
const clearedFieldLabels: Record<string, string> = {
  reference: 'a referência',
  sku: 'o SKU',
  licensePlate: 'a placa',
};

/**
 * Campos de identidade removidos porque a base de destino sincroniza com
 * sistema externo. `slug`/`externalId` também vêm em `clearedFields`, mas são
 * internos — o usuário não os conhece e não deve ser avisado sobre eles.
 */
const identityFieldsCleared = (results: DatabaseDocumentTransferItemResult[]): string[] => {
  const found = new Set<string>();
  for (const item of results) {
    for (const field of item.clearedFields ?? []) {
      const label = clearedFieldLabels[field];
      if (label) found.add(label);
    }
  }
  return Array.from(found);
};

export const TransferDocumentsDialog: React.FC<TransferDocumentsDialogProps> = ({
  isOpen,
  onClose,
  mode,
  sourceDatabaseId,
  databaseType,
  documentIds,
  titleById,
  onTransferred,
}) => {
  const [targetDatabaseId, setTargetDatabaseId] = useState('');
  const [result, setResult] = useState<DatabaseDocumentTransferResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Hooks condicionados ao `isOpen`: eles rodam ANTES do `if (!isOpen) return null`
  // lá embaixo (CLAUDE.md NUNCA #14) — sem `enabled` a lista de bases seria
  // buscada com o modal fechado, uma vez por linha da tabela.
  const { data: databases = [], isLoading: isLoadingDatabases } = useDatabasesDropdown(
    databaseType,
    isOpen
  );

  const copyMutation = useCopyDatabaseDocuments();
  const moveMutation = useMoveDatabaseDocuments();
  const mutation = mode === 'copy' ? copyMutation : moveMutation;
  const isPending = mutation.isPending;

  const text = MODE_TEXT[mode];

  // Reset ao abrir — senão o destino escolhido e o resultado de uma operação
  // anterior vazam para a próxima abertura.
  useEffect(() => {
    if (isOpen) {
      setTargetDatabaseId('');
      setResult(null);
      setError(null);
    }
  }, [isOpen]);

  const options: SelectOption[] = useMemo(
    () =>
      databases
        // A base atual sai da lista nos dois modos: copiar para ela é o
        // "Duplicar aqui" (que não abre modal) e mover para ela é recusado.
        .filter((database) => database.id !== sourceDatabaseId)
        .map((database) => ({ value: database.id, label: buildDatabaseTargetLabel(database) })),
    [databases, sourceDatabaseId]
  );

  const titleFor = (documentId: string): string =>
    titleById[documentId] || `Registro ${documentId.slice(-6)}`;

  const rejectedItems = result ? result.results.filter((item) => item.status === 'rejected') : [];
  const indexPendingCount = result
    ? result.results.filter((item) => item.indexPending === true).length
    : 0;

  const canConfirm = !!targetDatabaseId && documentIds.length > 0 && !isPending && result === null;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setError(null);
    try {
      const outcome = await mutation.mutateAsync({
        sourceDatabaseId,
        targetDatabaseId,
        documentIds,
      });
      setResult(outcome);
      if (outcome.summary.succeeded > 0) {
        toast.success(
          `${outcome.summary.succeeded} ${outcome.summary.succeeded === 1 ? text.doneOne : text.doneMany}`
        );
        onTransferred?.();
      }
      if (outcome.summary.rejected > 0) {
        toast.error(
          `${outcome.summary.rejected} ${outcome.summary.rejected === 1 ? text.failOne : text.failMany}`
        );
      }
      const cleared = identityFieldsCleared(outcome.results);
      if (cleared.length > 0) {
        toast.info(
          `Em ${cleared.join(' e ')}: o valor foi removido porque a base de destino ` +
            'sincroniza com um sistema externo. Preencha de novo se precisar.'
        );
      }
    } catch (err) {
      // O request vai com `_handleErrorLocally`, então o toast global está
      // suprimido — esta é a ÚNICA mensagem do erro de lote (404 da base de
      // destino, 422 de lista vazia/acima de 50).
      setError(extractApiFieldErrors(err).message);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* transferência: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
      data-testid="transfer-documents-dialog"
    >
      <div
        className="relative rounded-lg shadow-xl max-w-md w-full max-h-[85vh] flex flex-col"
        style={{ backgroundColor: '#ffffff', ...LIGHT_THEME_OVERRIDE }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b flex-none"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-none"
              style={{ backgroundColor: '#F3E8FF' }}
            >
              <Icon name={text.icon} className="w-5 h-5" style={{ color: '#7C3AED' }} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                {text.title(documentIds.length)}
              </h2>
              <p className="text-sm" style={{ color: '#64748b' }}>
                {text.subtitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors flex-none"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
            disabled={isPending}
            title="Fechar"
            data-testid="transfer-documents-close"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo — campos UM ABAIXO DO OUTRO */}
        <div className="p-6 flex-1 overflow-y-auto flex flex-col gap-4">
          <Select
            label="Base de destino"
            description="Só bases do mesmo tipo aparecem aqui — o backend confere de novo antes de gravar."
            value={targetDatabaseId}
            onChange={setTargetDatabaseId}
            options={options}
            placeholder={isLoadingDatabases ? 'Carregando bases…' : 'Escolha a base de destino'}
            disabled={isPending || result !== null || options.length === 0}
            data-testid="transfer-documents-target"
          />

          {!isLoadingDatabases && options.length === 0 && (
            <p className="text-sm" style={{ color: '#64748b' }} data-testid="transfer-documents-no-target">
              Não existe outra base deste tipo. Crie uma base do mesmo tipo para usar esta ação.
            </p>
          )}

          {/* Aviso FIXO do modo mover (spec §7) — sem promessa sobre o site. */}
          {mode === 'move' && (
            <div
              className="flex items-start gap-2 rounded-lg p-3 text-xs"
              style={{ backgroundColor: '#fef3c7', color: '#92400e' }}
              data-testid="transfer-documents-move-warning"
            >
              <Icon name="alert-triangle" className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                O registro sai da base de origem — some das listagens e das buscas do agente
                ligadas a ela.
              </span>
            </div>
          )}

          {/* Copiar dispara as automações de "registro criado" (D9). */}
          {mode === 'copy' && (
            <div
              className="flex items-start gap-2 rounded-lg p-3 text-xs"
              style={{ backgroundColor: '#eff6ff', color: '#1e40af' }}
              data-testid="transfer-documents-copy-warning"
            >
              <Icon name="info" className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {documentIds.length === 1
                  ? 'Será criado 1 registro novo, e as automações de "registro criado" da base de destino disparam para ele. O código de referência nasce em branco.'
                  : `Serão criados ${documentIds.length} registros novos, e as automações de "registro criado" da base de destino disparam para cada um. O código de referência nasce em branco.`}
              </span>
            </div>
          )}

          {error && (
            <div
              className="p-3 rounded-lg flex items-start gap-3"
              style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA' }}
            >
              <Icon name="alert-triangle" className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
              <p className="text-sm" style={{ color: '#991B1B' }} data-testid="transfer-documents-error">
                {error}
              </p>
            </div>
          )}

          {/* Resultado item a item — id traduzido para título com a página já carregada. */}
          {result && (
            <div className="flex flex-col gap-3">
              {result.summary.succeeded > 0 && (
                <div
                  className="flex items-start gap-2 rounded-lg p-3 text-sm"
                  style={{ backgroundColor: '#DCFCE7', color: '#166534' }}
                  data-testid="transfer-documents-summary"
                >
                  <Icon name="check-circle" className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {result.summary.succeeded}{' '}
                    {result.summary.succeeded === 1 ? text.doneOne : text.doneMany}
                  </span>
                </div>
              )}

              {rejectedItems.length > 0 && (
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                  data-testid="transfer-documents-rejected"
                >
                  <div className="flex items-start gap-2">
                    <Icon name="alert-circle" className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">
                      {rejectedItems.length}{' '}
                      {rejectedItems.length === 1 ? text.failOne : text.failMany}:
                    </span>
                  </div>
                  <ul className="mt-1.5 pl-6 list-disc space-y-1">
                    {rejectedItems.map((item) => (
                      <li key={item.documentId} data-testid={`transfer-documents-rejected-${item.documentId}`}>
                        <span className="font-semibold">{titleFor(item.documentId)}</span>{' '}
                        {item.reasonCode
                          ? REJECTION_LABELS[item.reasonCode]
                          : 'foi recusado pelo servidor'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {indexPendingCount > 0 && (
                <div
                  className="flex items-start gap-2 rounded-lg p-3 text-xs"
                  style={{ backgroundColor: '#fef3c7', color: '#92400e' }}
                  data-testid="transfer-documents-index-pending"
                >
                  <Icon name="alert-triangle" className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {indexPendingCount === 1
                      ? '1 registro pode demorar a aparecer nas buscas do agente. Repetir a mesma ação corrige.'
                      : `${indexPendingCount} registros podem demorar a aparecer nas buscas do agente. Repetir a mesma ação corrige.`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 border-t flex-none"
          style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
        >
          {result ? (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#7C3AED' }}
              data-testid="transfer-documents-done"
            >
              Fechar
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: '#1e293b', backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}
                data-testid="transfer-documents-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={() => { void handleConfirm(); }}
                disabled={!canConfirm}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#7C3AED' }}
                data-testid="transfer-documents-confirm"
              >
                {isPending ? (
                  <>
                    <Icon name="loader" className="w-4 h-4 animate-spin" />
                    <span>{text.pendingLabel}</span>
                  </>
                ) : (
                  <>
                    <Icon name={text.icon} className="w-4 h-4" />
                    <span>{text.confirmLabel}</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TransferDocumentsDialog;
